import React, { useEffect } from 'react';
import {
  MUIDataTable,
  MUIDataTableColumn,
  SectionHeader,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { Box, Theme, useTheme } from '@mui/material';
import {CSSInterpolation, GlobalStyles} from "tss-react";
import { makeStyles } from 'tss-react/mui';

import { TFunction, useTranslation } from 'react-i18next';
import { TransactionRecordDetailsListingObjectModel } from '../../../api-refactored/transaction/useGetTransactionDetailsListing';
import { SetTransactionAmountPrefix } from './TransactionDetails';

const useStyles = makeStyles()((theme) => ({
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  button: {
    padding: '0px !important',
  },
  table: {
    paddingBottom: theme.spacing(4),
  },
  tableText: {
    justifyContent: 'end',
  },
  transactionAmount: {
    textAlign: 'right',
  },
}));

interface DataTableParams {
  state: TableState;
  classes: Record<string,string>;
  dispatch: React.Dispatch<TableActionTypes>;
  t: TFunction<'translation'>;
  isPaymentTransactionsData: boolean;
}
const globalStyle = (theme: Theme): CSSInterpolation => ({
  '.MuiTableCell-root': {
    verticalAlign: 'middle',
  },
  '.MuiTableCell-head:last-child': {
    textAlign: 'end',
  },
  '.MuiTableCell-root:last-child': {
    textAlign: 'end',
  },
})
const useTableColumns = (params: DataTableParams): MUIDataTableColumn[] => [
  {
    name: 'examYear',
    label: params.t('transaction->>Candidate.Table.Column.ExamYear'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].examYear}
        </Typography>
      ),
    },
  },
  {
    name: 'transactionDesc',
    label: params.t('transaction->>Candidate.Table.Column.TransactionDescription'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].transactionDesc}
        </Typography>
      ),
    },
  },
  {
    name: 'transactionDate',
    label: params.t('transaction->>Candidate.Table.Column.TransactionDate'),
    options: {
      display: params.isPaymentTransactionsData,
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].transactionDate ?? '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'paymentDate',
    label: params.t('transaction->>Candidate.Table.Column.PaymentDate'),
    options: {
      display: params.isPaymentTransactionsData,
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].paymentDate ?? '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'paymentMode',
    label: params.t('transaction->>Candidate.Table.Column.PaymentMode'),
    options: {
      display: params.isPaymentTransactionsData,
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].paymentMode ?? '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'paymentReferenceNo',
    label: params.t('transaction->>Candidate.Table.Column.PaymentReferenceNo'),
    options: {
      display: params.isPaymentTransactionsData,
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <Typography variant="body2" className={params.classes.tableText}>
          {params.state.data[dataIndex].paymentReferenceNo ?? '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'transactionAmt',
    label: params.t('transaction->>Candidate.Table.Column.TransactionAmount'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) => (
        <SetTransactionAmountPrefix value={params.state.data[dataIndex].transactionAmt} />
      ),
    },
  },
];

interface TransactionDetailsListingTableProps {
  title: string;
  tableData?: TransactionRecordDetailsListingObjectModel[];
  totalAmount?: string;
  isPaymentTransactionsData?: boolean;
}

const TransactionDetailsListingTable = ({
  title,
  tableData,
  totalAmount,
  isPaymentTransactionsData = false,
}: TransactionDetailsListingTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable(undefined, true);

  const tableColumns = useTableColumns({
    state,
    classes,
    dispatch: tableDispatch,
    t,
    isPaymentTransactionsData,
  });

  useEffect(() => {
    if (tableData !== undefined) {
      let concatData: TransactionRecordDetailsListingObjectModel[] = [];
      if (tableData.length > 0) {
        const lastRow: TransactionRecordDetailsListingObjectModel = {
          examYear: '',
          transactionDesc: '',
          transactionDate: '',
          paymentDate: '',
          paymentMode: '',
          paymentReferenceNo: '',
          transactionAmt: totalAmount === undefined ? '' : totalAmount ?? '0.00',
        };
        concatData = [...tableData, lastRow];
      }
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: concatData,
        },
      } as TableActionTypes);
    }
  }, [tableDispatch, tableData, totalAmount]);

  return (
    <>
      <GlobalStyles
          styles={globalStyle(theme)}/>
      <Box className={classes.table}>
        <SectionHeader title={title} />
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          emptyDataLabel={t('transaction->>Candidate.Table.EmptyLabel')}
          options={{
            ...tOptions,
            filter: false,
            sort: true,
            pagination: false,
          }}
        />
      </Box>
    </>
  );
};

export default TransactionDetailsListingTable;
